'use client'
import { useEffect, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { useSelector } from "react-redux"
import moment from "moment"
import ImageWithFallback from "@/Component/Common/ImageWithFallback"
import BookmarkToggle from "@/Component/Common/InfluencerBox/BookmarkToggle"
import FavoriteToggle from "@/Component/Common/InfluencerBox/FavoriteToggle"
import { YoutubeSVG } from "@/Component/Common/SVGs/AuthSVG"
import { FacebookSVG, InfluencerInstagramSVG, InfluencerTiktokSVG, XiaohongshuSVG } from "@/Component/Common/SVGs/CommonSVG"
import { AppStore } from "@/lib/store"
import { formatNumber } from "@/Utils/Other/FormateNumbers"
import HeartIcon from '@/assets/images/heart.png';
import CommentIcon from '@/assets/images/comment.png';

const SaveBox = (props) => {
    const { data, campaignDetailData, favoriteList, setFavoriteList, setOpenSideModal, translations, listingData, index } = props
    const [isFavorite, setIsFavorite] = useState(data?.influencer?.favoritesCount === 1 ? true : false);
    const [isBookmarked, setIsBookmarked] = useState(data?.influencer?.isBookmarkAdded === 1 ? true : false);
    const userDetails: any = useSelector((state: AppStore) => state.userDetailSlice?.userDetails);
    const [hoveredIndex, setHoveredIndex] = useState(null);

    useEffect(() => {
        if (data?.influencer?.id && data?.influencer?.favoritesCount == 1) {
            setFavoriteList && setFavoriteList((prev) => {
                if (prev.length > 0) {
                    if (prev.includes(data?.influencer?.id)) {
                        return prev.filter((elem) => elem !== data?.influencer?.id)
                    } else {
                        return [...prev, data?.influencer?.id]
                    }
                } else {
                    return [data?.influencer?.id]
                }
            })
        }
    }, [data?.influencer?.id])

    const handleBoxClick = (event) => {
        if (event.target.tagName === 'BUTTON' || event.target.tagName === 'A' || event.target.closest('button') || event.target.closest('a')) {
            return;
        }
        setOpenSideModal(data);
    };

    const getSocialMediaLink = (type: string) => {
        const media = data?.influencer?.social_medias?.find((m) => m?.social_type === type);
        if (!media) return '#';

        switch (type) {
            case 'instagram':
                return `https://www.instagram.com/${media.social_user_name}`;
            case 'facebook':
                return `https://www.facebook.com/${media.social_user_name}`;
            case 'tiktok':
                return `https://www.tiktok.com/@${media.social_user_name}`;
            case 'youtube':
                return `https://www.youtube.com/@${media.social_user_name}`;
            case 'xiaohongshu':
                return `https://www.xiaohongshu.com/user/profile/${media.social_user_name}`;
            default:
                return '#';
        }
    };

    return (
        <>
            <div className={`${listingData ? 'justify-between' : 'flex-col'} group relative gap-7 border border-neutral-100 rounded-card p-4 flex cursor-pointer hover:border-primary/20 hover:shadow-float hover:-translate-y-0.5 bg-white shadow-soft transition-all duration-200`} onClick={handleBoxClick}>
                {data?.influencer?.isArchived == 1 && <div className='rounded-full overflow-hidden text-white bg-gradient-to-br from-secondary to-primary py-0.5 px-3 font-medium absolute -top-3.5 left-3.5 shadow-soft'>Archived</div>}
                <div className={`flex gap-2.5  ${listingData ? 'lg:w-1/5 items-start' : 'w-auto items-center'}`}>
                    <div className="min-w-16 w-16 h-16 rounded-full overflow-hidden bg-gray">
                        <ImageWithFallback src={data?.influencer?.image} alt='Profile' className='w-full h-full object-cover' height={70} width={70} />
                    </div>
                    <div className={`flex flex-col ${listingData ? 'gap-2' : ''}`}>
                        <h5 className="font-semibold text-base leading-6 line-clamp-1">{data?.influencer?.display_name}</h5>
                        <div className="flex items-center gap-1.5">
                            <span className="text-neutral-600 text-xs">{data?.influencer?.country}</span>
                            <div className="inline-block align-middle bg-neutral-300 w-1 h-1 rounded-full"></div>
                            {listingData ?
                                <div className="flex items-center justify-between gap-1 ">
                                    {(() => {
                                        const nonNullCategories = data?.influencer?.influencerCategories?.filter(cat => cat?.category !== null) || [];
                                        return (
                                            nonNullCategories.length > 0 && (
                                                <>
                                                    <span className={`${nonNullCategories.length > 1 ? 'line-clamp-1 max-2xl:max-w-44 2xl:max-w-40' : 'line-clamp-1 w-full'} text-xs`}>
                                                        {nonNullCategories[0]?.category?.translations?.[0]?.title}
                                                    </span>
                                                    {nonNullCategories.length > 1 && (
                                                        <span
                                                            className='relative text-primary underline cursor-pointer whitespace-nowrap text-xs'
                                                            onMouseEnter={() => setHoveredIndex(index)}
                                                            onMouseLeave={() => setHoveredIndex(null)}
                                                        >
                                                            {` +${nonNullCategories.length - 1} more`}
                                                            {hoveredIndex === index && (
                                                                <div className="absolute top-full right-0 bg-white text-black p-2 rounded shadow-lg w-max z-10">
                                                                    {nonNullCategories.slice(1).map((category, idx) => (
                                                                        <div key={idx} className="text-xs">{category?.category?.translations?.[0]?.title}</div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </span>
                                                    )}
                                                </>
                                            )
                                        );
                                    })()}
                                </div> : <span className="text-neutral-600 text-xs">{data?.influencer?.engagement_ratio == '0.00' ? '--' : Number(data?.influencer?.engagement_ratio).toFixed(1)} % {translations?.engagementRatio} </span>}
                        </div>
                        {listingData &&
                            <div className="flex gap-5">
                                {data?.influencer?.platforms?.length > 0 ?
                                    <>
                                        {data?.influencer?.platforms?.includes("instagram") ? <Link target='_blank' href={getSocialMediaLink('instagram')}><InfluencerInstagramSVG /></Link> : null}
                                        {data?.influencer?.platforms?.includes("facebook") ? <Link target='_blank' href={getSocialMediaLink('facebook')}><FacebookSVG /></Link> : null}
                                        {data?.influencer?.platforms?.includes("tiktok") ? <Link target='_blank' href={getSocialMediaLink('tiktok')}><InfluencerTiktokSVG /></Link> : null}
                                        {data?.influencer?.platforms?.includes("youtube") ? <Link target='_blank' href={getSocialMediaLink('youtube')}><YoutubeSVG /></Link> : null}
                                        {data?.influencer?.platforms?.includes("xiaohongshu") ? <Link target='_blank' href={getSocialMediaLink('xiaohongshu')}><XiaohongshuSVG /></Link> : null}
                                    </>
                                    : null}
                            </div>
                        }
                    </div>
                </div>
                {listingData ?
                    <div className={`flex items-center justify-between ${listingData ? 'gap-4 2xl:gap-14 flex-col !items-start lg:flex-row' : ''}`}>
                        <div className="grid grid-cols-3 gap-3 w-full lg:w-auto">
                            {
                                data?.influencer?.socialMediaPosts?.length > 0 ?
                                    data?.influencer?.socialMediaPosts?.map((post) => (
                                        <div className='relative w-20 h-20 '>
                                            <ImageWithFallback src={post?.image_url ? post?.image_url : null} alt='Brand' width={70} height={70} className='object-cover rounded w-full h-full ' />
                                            <div className="content absolute bottom-1 left-1 hidden xl:block">
                                                <div>
                                                    <p className='mb-0.5 inline-block bg-black bg-opacity-60 text-white py-0.5 px-1 rounded text-[8px] '>{post?.created_time ? moment(post?.created_time).format("MMMM DD, yyy") : null}</p>
                                                    <div className="flex gap-1 bg-black bg-opacity-60 rounded text-white py-0.5 px-1">
                                                        <p className='flex gap-1 items-center'>
                                                            <Image src={HeartIcon} alt='Heart' width={8} height={8} className='max-h-2' />
                                                            <span className='text-[8px] block'>{formatNumber(post?.like_count)}</span>
                                                        </p>
                                                        <p className='flex gap-1 items-center'>
                                                            <Image src={CommentIcon} alt='Heart' width={8} height={8} className='max-h-2' />
                                                            <span className='text-[8px] block'>{formatNumber(post?.comment_count)}</span>
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                    : <div className='relative col-span-12 md:col-span-4'>{translations?.noPostFound}</div>
                            }
                        </div>
                    </div>
                    :
                    <div className="flex items-center justify-between ">
                        <div className="text-xs font-normal text-neutral-700 whitespace-nowrap text-ellipsis overflow-hidden">
                            {(() => {
                                const validCategories = data?.influencer?.influencerCategories?.filter(item => item?.category?.translations?.[0]?.title);
                                return (
                                    <div className="text-sm font-normal text-neutral-700 whitespace-nowrap text-ellipsis overflow-hidden">
                                        <span className="text-sm font-semibold">{translations?.topic}: </span>
                                        {validCategories?.length > 0
                                            ? validCategories.map((item, index) => (
                                                <span key={item.id}>
                                                    {item?.category?.translations?.[0]?.title}
                                                    {index !== validCategories.length - 1 && ', '}
                                                </span>
                                            ))
                                            : "-"}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                }
                <div className="flex justify-between items-center gap-10">
                    {listingData ?
                        <div className={`flex items-center ${listingData ? '2xl:gap-14 gap-3 ' : 'gap-7'}`}>
                            <div className='flex flex-col justify-start w-full'>
                                <label className='font-semibold'>{formatNumber(data?.influencer?.total_followers)}</label>
                                <span className='whitespace-nowrap'>{translations?.followers}</span>
                            </div>
                            <div className='flex flex-col justify-start'>
                                <label className='font-semibold'>{data?.influencer?.engagement_ratio == '0.00' ? '--' : Number(data?.influencer?.engagement_ratio).toFixed(1)} %</label>
                                <span className='whitespace-nowrap'>{translations?.engagementRatio}</span>
                            </div>
                        </div>
                        :
                        <div className="flex gap-5">
                            {data?.influencer?.platforms?.length > 0 ?
                                <>
                                    {data?.influencer?.platforms?.includes("instagram") ? <Link target='_blank' href={getSocialMediaLink('instagram')}><InfluencerInstagramSVG /></Link> : null}
                                    {data?.influencer?.platforms?.includes("facebook") ? <Link target='_blank' href={getSocialMediaLink('facebook')}><FacebookSVG /></Link> : null}
                                    {data?.influencer?.platforms?.includes("tiktok") ? <Link target='_blank' href={getSocialMediaLink('tiktok')}><InfluencerTiktokSVG /></Link> : null}
                                    {data?.influencer?.platforms?.includes("youtube") ? <Link target='_blank' href={getSocialMediaLink('youtube')}><YoutubeSVG /></Link> : null}
                                    {data?.influencer?.platforms?.includes("xiaohongshu") ? <Link target='_blank' href={getSocialMediaLink('xiaohongshu')}><XiaohongshuSVG /></Link> : null}
                                </>
                                : null}
                        </div>
                    }
                    {data?.influencer?.favoritesCount && <FavoriteToggle data={data?.influencer} isFavorite={isFavorite} setIsFavorite={setIsFavorite} favoriteList={favoriteList} setFavoriteList={setFavoriteList} />}
                    {data?.influencer?.isBookmarkAdded && <BookmarkToggle campaignDetailData={campaignDetailData} data={data?.influencer} isBookmarked={isBookmarked} setIsBookmarked={setIsBookmarked} bookmarkId={data?.id} />}
                </div>
            </div>
        </>

    )
}

export default SaveBox
